"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import { useState } from "react";
import { cn } from "@/lib/cn";
import { spring } from "@/lib/motion";
import type { Game } from "@/lib/data";
import { Icon } from "@/components/ui/Icon";
import { RemoteImage } from "@/components/ui/RemoteImage";
import { useContent } from "@/components/shell/ContentContext";
import { usePlayer } from "@/components/shell/PlayerContext";

const TILE_SIZES = "(max-width: 640px) 45vw, (max-width: 1024px) 24vw, 210px";

/* The kit's poster tile: saturated art, RTP top-left, tag top-right, the
   title set as a compressed display block with the studio underneath. */
export function GameTile({
  game,
  size = "md",
  showTag = true,
  className,
}: {
  game: Game;
  size?: "md" | "lg" | "xl";
  showTag?: boolean;
  className?: string;
}) {
  const { flags } = useContent();
  const player = usePlayer();
  const [pops, setPops] = useState(0);
  const locked = Boolean(game.locked);
  const favorite = player.isFavorite(game.id);
  const titleSize = size === "xl" ? "text-[30px]" : size === "lg" ? "text-[17.5px]" : "text-[17px]";

  const body = (
    <motion.div
      whileHover={locked ? undefined : { y: -6, scale: 1.025 }}
      transition={spring}
      className={cn(
        "group/tile relative aspect-[158/209] w-full overflow-hidden rounded-lg",
        !locked && "hover:z-10",
        className,
      )}
      style={{
        background: `linear-gradient(155deg, ${game.from} 0%, ${game.to} 100%)`,
      }}
    >
      {/* art stand-in: layered radial light + vignette gives the posters depth */}
      <span
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(85% 55% at 50% 22%, rgba(255,255,255,0.34) 0%, transparent 60%), radial-gradient(120% 80% at 50% 118%, rgba(0,0,0,0.62) 0%, transparent 62%)",
        }}
      />
      <span className="absolute inset-0 opacity-[0.09] mix-blend-overlay [background-image:repeating-linear-gradient(115deg,#fff_0_2px,transparent_2px_7px)]" />

      {game.thumbnailUrl && (
        <RemoteImage src={game.thumbnailUrl} alt={game.title} sizes={TILE_SIZES} />
      )}

      {/* sheen sweeps across on hover */}
      {!locked && (
        <span className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 -skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent opacity-0 transition-all duration-700 ease-out group-hover/tile:left-[120%] group-hover/tile:opacity-100" />
      )}

      <div className="relative flex h-full flex-col justify-end p-2.5">
        <div className="absolute inset-x-0 top-0 flex items-start justify-between p-[6px]">
          {game.rtp ? (
            <span className="flex h-[16px] items-center justify-center rounded-sm bg-elevation-3 px-[3px] text-[9px] font-bold leading-[1.5] text-fg">
              RTP {game.rtp}
            </span>
          ) : (
            <span />
          )}
          <span className="flex items-start gap-[2px]">
            {showTag && game.tag && (
              <span className="flex h-[16px] items-center justify-center rounded-sm bg-error px-[3px] text-[9px] font-bold leading-[1.5] text-fg">
                {game.tag}
              </span>
            )}
            {flags.favoritesEnabled && player.enabled && !locked && (
              <motion.button
                type="button"
                aria-label={favorite ? `Remove ${game.title} from favorites` : `Add ${game.title} to favorites`}
                aria-pressed={favorite}
                whileTap={{ scale: 0.82 }}
                transition={spring}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setPops((count) => count + 1);
                  player.toggleFavorite(game.id);
                }}
                className={cn(
                  "grid h-[24px] w-[24px] place-items-center rounded-sm bg-elevation-3 transition-colors",
                  favorite ? "text-warning-fg" : "text-fg hover:text-warning-fg",
                )}
              >
                <motion.span
                  key={pops}
                  initial={pops === 0 ? false : { scale: 0.4, rotate: -45 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={spring}
                  className="grid place-items-center"
                >
                  <Icon name="star" size={11} />
                </motion.span>
              </motion.button>
            )}
          </span>
        </div>

        {!game.thumbnailUrl && (
          <>
            <h3
              className={cn(
                "text-center font-black uppercase leading-[0.94] tracking-[-0.02em] text-white [text-shadow:0_2px_10px_rgba(0,0,0,0.5)]",
                titleSize,
              )}
            >
              {game.title}
            </h3>
            <p className="mt-1.5 text-center text-[8px] font-bold uppercase tracking-[0.14em] text-white/70">
              {game.studio}
            </p>
          </>
        )}
      </div>

      {locked && <LockedOverlay />}

      {!locked && (
        <span className="pointer-events-none absolute inset-0 grid place-items-center opacity-0 transition-opacity duration-300 group-hover/tile:opacity-100">
          <span className="glow-brand grid h-[38px] w-[38px] place-items-center rounded-full bg-brand text-fg-on-primary">
            <Icon name="rocket" size={18} />
          </span>
        </span>
      )}
    </motion.div>
  );

  return <Link href={`/game/${game.slug ?? game.id}`} className="block">{body}</Link>;
}

function LockedOverlay() {
  return (
    <div className="absolute inset-0 grid place-items-center bg-black/72 backdrop-grayscale">
      <div className="flex flex-col items-center gap-1.5 px-3 text-center">
        <Icon name="lock" size={16} className="text-white/85" />
        <p className="text-[12px] font-bold leading-tight text-white/85">
          Not available
          <br />
          in your region
        </p>
      </div>
    </div>
  );
}
